import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { UnseenReviewBar, UnseenDot } from "@/components/admin/UnseenReviewBar";
import { useMarkSeenWhenDisplayed } from "@/hooks/useAdminUnseen";


/**
 * Enrollment alerts — the authenticated confirmation surface for every action
 * offered in the 10 AM / 4 PM admin emails. The email links here; the action
 * itself is executed by the `enrollment-alert-actions` edge function, which
 * independently re-verifies the admin role. Nothing on this page changes money.
 */

interface CheckRow {
  id: string;
  class_slug: string;
  class_title: string | null;
  session_label: string;
  first_meeting_date: string;
  check_date: string;
  checkpoint: "morning" | "afternoon";
  paid_enrolled: number;
  min_required: number;
  below_minimum: boolean;
  admin_action: "none" | "reminder_sent" | "postponed";
  parent_recipient_count: number | null;
  metadata: Record<string, unknown> | null;
}

interface InterestAlertRow {
  id: string;
  class_slug: string;
  class_title: string | null;
  distinct_families: number;
  threshold: number;
  breakdown: Array<{ label: string; count: number }>;
  alert_sent_at: string | null;
  cleared_at: string | null;
}

/** Raw internal text we should never show an administrator. */
function isInternalNoise(msg: string): boolean {
  return /non-2xx|FunctionsHttpError|stack|at .*\.ts:\d+|pg[a-z_]*:|permission denied for|SQLSTATE/i.test(
    msg,
  );
}

async function call(action: string, payload: Record<string, unknown> = {}) {
  const { data, error } = await supabase.functions.invoke("enrollment-alert-actions", {
    body: { action, ...payload },
  });

  // Supabase surfaces non-2xx as a generic error. The function always answers
  // with a structured JSON body, so read the real (safe) message from it.
  if (error) {
    let serverMessage = "";
    const res = (error as any)?.context;
    if (res && typeof res.json === "function") {
      try {
        const body = await res.json();
        if (body?.error) serverMessage = String(body.error);
      } catch { /* body not JSON — fall through */ }
    }
    if (!serverMessage && (data as any)?.error) serverMessage = String((data as any).error);
    if (!serverMessage || isInternalNoise(serverMessage)) {
      serverMessage = "The server could not complete this request. Please try again.";
    }
    throw new Error(serverMessage);
  }
  if (data?.error) throw new Error(String(data.error));
  return data;
}

interface ReminderPreview {
  recipients: number;
  skipped: string[];
  oneEmailPerFamily: boolean;
  fingerprint: string;
  classes: Array<{
    title: string;
    sessionLabel: string;
    firstMeetingDate: string;
    paidEnrolled: number;
    minRequired: number;
    excludedEnrolledFamilies: number;
  }>;
  recentlyOpened: Array<{
    title: string;
    sessionLabel: string;
    firstMeetingDate: string;
    firstMeetingLabel: string;
    price: string | null;
    enrollmentOpenedAt: string;
  }>;
  recipientList: Array<{ name: string | null; email: string; classes: string[] }>;
  /** Selection the preview was computed for — used to invalidate on change. */
  selectionKey: string;
}

const formatDate = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

export default function EnrollmentAlertsPage() {
  const [params] = useSearchParams();
  const [checks, setChecks] = useState<CheckRow[]>([]);
  const [interest, setInterest] = useState<InterestAlertRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [preview, setPreview] = useState<ReminderPreview | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await call("list");
      setChecks(data.checks ?? []);
      setInterest(data.interestAlerts ?? []);
      setRemindersEnabled(data.remindersEnabled !== false);
    } catch (e) {
      toast({ title: "Could not load alerts", description: String((e as Error).message), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  // Deep link from the email: pre-select the class Savanna clicked.
  useEffect(() => {
    const id = params.get("check");
    if (id) setSelected(new Set([id]));
  }, [params]);

  const morning = useMemo(
    () => checks.filter((c) => c.checkpoint === "morning" && c.below_minimum),
    [checks],
  );
  const afternoon = useMemo(() => checks.filter((c) => c.checkpoint === "afternoon"), [checks]);

  // Only the rows actually on screen get acknowledged.
  useMarkSeenWhenDisplayed(
    useMemo(
      () => [...morning.map((c) => `emc:${c.id}`), ...interest.map((a) => `cia:${a.id}`)],
      [morning, interest],
    ),
    !loading,
  );


  const eligibleIds = useMemo(
    () => morning.filter((c) => selected.has(c.id) && c.admin_action === "none").map((c) => c.id),
    [morning, selected],
  );

  /** Alert/recipient/selection state signature — any change invalidates the preview. */
  const stateKey = useMemo(
    () =>
      eligibleIds
        .map((id) => {
          const c = morning.find((m) => m.id === id)!;
          return `${c.id}:${c.paid_enrolled}:${c.admin_action}`;
        })
        .sort()
        .join("|"),
    [eligibleIds, morning],
  );

  const previewStale = preview !== null && preview.selectionKey !== stateKey;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Any change to selection or underlying alert state forces a fresh preview.
  useEffect(() => {
    setPreview((p) => (p && p.selectionKey !== stateKey ? null : p));
  }, [stateKey]);

  const runPreview = async () => {
    if (eligibleIds.length === 0) {
      toast({ title: "Select at least one class that hasn't been sent yet" });
      return;
    }
    setBusy("preview");
    try {
      const res = await call("preview", { kind: "reminder", checkIds: eligibleIds });
      const p = res.preview ?? {};
      setPreview({
        recipients: Number(res.recipients ?? 0),
        skipped: res.skipped ?? [],
        oneEmailPerFamily: p.oneEmailPerFamily !== false,
        fingerprint: String(p.fingerprint ?? ""),
        classes: p.classes ?? [],
        recentlyOpened: p.recentlyOpened ?? [],
        recipientList: p.recipients ?? [],
        selectionKey: stateKey,
      });
    } catch (e) {
      toast({ title: "Preview failed", description: String((e as Error).message), variant: "destructive" });
    } finally { setBusy(null); }
  };

  const sendReminder = async () => {
    if (!remindersEnabled || !preview || previewStale || preview.recipients === 0) return;
    const ids = eligibleIds;
    if (ids.length === 0) return;
    setBusy("reminder");
    try {
      const res = await call("send_reminder", { checkIds: ids });
      toast({ title: "Enrollment reminder sent", description: `${res.recipients} families emailed.` });
      setPreview(null);
      await load();
    } catch (e) {
      toast({ title: "Not sent", description: String((e as Error).message), variant: "destructive" });
    } finally { setBusy(null); }
  };

  const postpone = async (row: CheckRow) => {
    const ok = window.confirm(
      `Postpone ${row.class_title}?\n\nThis closes paid registration for "${row.session_label}", moves the class to Coming Soon with interest open, and emails the families already registered. No refund, credit, Stripe, ledger or payroll change happens.`,
    );
    if (!ok) return;
    setBusy(row.id);
    try {
      const res = await call("postpone_class", { checkId: row.id });
      toast({ title: `${row.class_title} postponed`, description: `${res.familiesNotified} families notified.` });
      await load();
    } catch (e) {
      toast({ title: "Not postponed", description: String((e as Error).message), variant: "destructive" });
    } finally { setBusy(null); }
  };


  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      <header>
        <h1 className="font-heading text-3xl font-bold text-foreground">Enrollment Alerts</h1>
        <p className="text-muted-foreground mt-1 max-w-2xl">
          Minimum-enrollment checkpoints run at 10:00 AM and 4:00 PM Pacific on the day before a
          class's first meeting. Nothing here sends to families or changes a class until you
          confirm it.
        </p>
      </header>

      <UnseenReviewBar
        badge="enrollment_alerts"
        note="Marking these read only clears the sidebar bubble — it does not close registration or resolve the enrollment warning."
      />



      <Card>
        <CardHeader className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>10:00 AM — below minimum</CardTitle>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Button
              variant="outline"
              className="w-full whitespace-normal sm:w-auto"
              onClick={runPreview}
              disabled={busy !== null || morning.length === 0}
            >
              {busy === "preview" ? "Checking…" : "Preview Enrollment Reminder"}
            </Button>
            <Button
              onClick={sendReminder}
              className="w-full whitespace-normal sm:w-auto"
              disabled={
                busy !== null ||
                !remindersEnabled ||
                !preview ||
                previewStale ||
                preview.recipients === 0
              }
            >
              {busy === "reminder"
                ? "Sending…"
                : preview && !previewStale
                  ? `Send Enrollment Reminder (${preview.recipients})`
                  : "Send Enrollment Reminder"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {!remindersEnabled && !loading && (
            <div className="rounded-md border border-dashed bg-muted/40 p-3 text-sm">
              <p className="font-medium">Reminder emails are switched off.</p>
              <p className="text-muted-foreground">
                Enrollment reminders to families are currently disabled in Settings → Email
                Controls. You can still run a preview — sending stays disabled.
              </p>
              <Button asChild variant="outline" size="sm" className="mt-2">
                <Link to="/admin/settings">Open Settings → Email Controls</Link>
              </Button>
            </div>
          )}

          {preview && (
            <div className="rounded-md border bg-muted/30 p-4 space-y-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <p className="font-semibold">Preview — nothing has been sent</p>
                <Badge variant={previewStale ? "destructive" : "secondary"}>
                  {previewStale ? "Out of date — preview again" : "Ready"}
                </Badge>
              </div>

              <p className="font-semibold">Below Minimum Classes</p>
              <div className="space-y-1">
                {preview.classes.map((c) => (
                  <div key={`${c.title}-${c.sessionLabel}`} className="rounded-md border border-border/60 bg-background/60 p-3">
                    <p className="font-medium">{c.title}</p>
                    <p>{c.paidEnrolled} / {c.minRequired} paid enrollments · {Math.max(0, c.minRequired - c.paidEnrolled)} more needed</p>
                    <p className="text-muted-foreground">{c.sessionLabel} · First meeting {formatDate(c.firstMeetingDate)}</p>
                    <p className="text-muted-foreground">{c.excludedEnrolledFamilies} already-enrolled famil{c.excludedEnrolledFamilies === 1 ? "y" : "ies"} excluded</p>
                  </div>
                ))}
              </div>

              <div className="space-y-1">
                <p className="font-semibold">Recently Opened for Enrollment</p>
                {preview.recentlyOpened.length === 0 ? (
                  <p className="text-muted-foreground">
                    No classes opened for enrollment in the last 21 days.
                  </p>
                ) : (
                  preview.recentlyOpened.map((c) => (
                    <p key={`${c.title}-${c.sessionLabel}`}>
                      <span className="font-medium">{c.title}</span> — enrollment is now open ·{" "}
                      {c.sessionLabel} · first meeting {c.firstMeetingLabel}
                      {c.price ? ` · ${c.price}` : ""}
                    </p>
                  ))
                )}
              </div>

              <p className="font-medium">
                This will send the enrollment reminder to {preview.recipients} famil
                {preview.recipients === 1 ? "y" : "ies"}.
              </p>
              <p className="text-muted-foreground">
                {preview.oneEmailPerFamily
                  ? "Each family receives ONE combined email listing every class they are not already enrolled in."
                  : "Each family receives one email per class."}
              </p>

              {preview.recipients === 0 && (
                <p className="text-destructive">
                  There are currently no eligible families to receive this reminder.
                </p>
              )}

              {preview.recipientList.length > 0 && (
                <div className="max-h-56 overflow-auto rounded border bg-background p-2">
                  {preview.recipientList.map((r) => (
                    <p key={r.email} className="text-xs">
                      {r.name ? `${r.name} — ` : ""}{r.email}
                      <span className="text-muted-foreground"> · {r.classes.join(", ")}</span>
                    </p>
                  ))}
                </div>
              )}

              {preview.skipped.length > 0 && (
                <p className="text-muted-foreground">
                  Skipped: {preview.skipped.join(" · ")}
                </p>
              )}
            </div>
          )}

          {loading && <p className="text-muted-foreground text-sm">Loading…</p>}
          {!loading && morning.length === 0 && (
            <p className="text-muted-foreground text-sm">No classes below minimum right now.</p>
          )}
          {morning.map((c) => (
            <div key={c.id} className="flex flex-col items-start gap-3 rounded-md border p-3 sm:flex-row sm:items-center">
              <Checkbox
                checked={selected.has(c.id)}
                onCheckedChange={() => toggle(c.id)}
                disabled={c.admin_action !== "none"}
              />
              <div className="flex-1">
                <p className="font-semibold flex items-center gap-2">
                  {c.class_title} <UnseenDot itemKey={`emc:${c.id}`} />
                </p>

                <p className="text-sm">{c.paid_enrolled} / {c.min_required} paid enrollments · {Math.max(0, c.min_required - c.paid_enrolled)} more needed</p>
                <p className="text-sm text-muted-foreground">
                  {c.session_label} · First meeting {formatDate(c.first_meeting_date)}
                </p>
              </div>
              {c.admin_action === "reminder_sent" && (
                <Badge variant="secondary">Reminder sent · {c.parent_recipient_count ?? 0} families</Badge>
              )}
              {c.admin_action === "postponed" && <Badge variant="destructive">Postponed</Badge>}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>4:00 PM — final check</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!loading && afternoon.length === 0 && (
            <p className="text-muted-foreground text-sm">No afternoon checks recorded.</p>
          )}
          {afternoon.map((c) => (
            <div key={c.id} className="flex flex-col items-start gap-3 rounded-md border p-3 sm:flex-row sm:items-center">
              <div className="flex-1">
                <p className="font-semibold">{c.class_title}</p>
                <p className="text-sm">{c.paid_enrolled} / {c.min_required} paid enrollments · {Math.max(0, c.min_required - c.paid_enrolled)} more needed</p>
                <p className="text-sm text-muted-foreground">
                  {c.session_label} · First meeting {formatDate(c.first_meeting_date)}
                </p>
              </div>
              {!c.below_minimum ? (
                <Badge variant="secondary">Minimum met — no action needed</Badge>
              ) : c.admin_action === "postponed" ? (
                <Badge variant="destructive">Postponed</Badge>
              ) : (
                <Button variant="destructive" disabled={busy !== null} onClick={() => postpone(c)}>
                  {busy === c.id ? "Working…" : "Postpone Class"}
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>High interest</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!loading && interest.length === 0 && (
            <p className="text-muted-foreground text-sm">No classes have crossed the interest threshold.</p>
          )}
          {interest.map((a) => (
            <div key={a.id} className="rounded-md border p-3">
              <p className="font-semibold flex items-center gap-2">
                {a.class_title} — {a.distinct_families} families interested
                <UnseenDot itemKey={`cia:${a.id}`} />
              </p>

              <p className="text-sm text-muted-foreground">
                {(a.breakdown ?? []).map((b) => `${b.label} — ${b.count}`).join(" · ") || "No option breakdown"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {a.cleared_at
                  ? "Cleared — dropped back below threshold"
                  : a.alert_sent_at
                    ? `Alert sent ${new Date(a.alert_sent_at).toLocaleString()}`
                    : "Alert pending"}
              </p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
